import { useState, createElement, useEffect } from 'react'

import { GameId } from '@common'
import cx from 'classnames'
import { AnimatePresence, LazyMotion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import { useGameImages } from '@/components/games/images'
import { LOGIN_STATUS } from '@/features/user/userSlice'
import { setOnboardingCompletedCookie, hasCompletedOnboarding } from '@/utils/cookies'
import { useAppSelector } from '@/utils/useAppSelector'

import { ExitIcon } from '../../../../../components/Icons/ExitIcon'
import { Footer } from './Footer'

import styles from './OnboardingModal.module.scss'

const features = () => import('@/animations/features').then((a) => a.default)

const getBgImage = (gameId: GameId) => {
  return {
    backgroundImage: `url('/images/${gameId}/login-prompt-bg.webp')`,
  }
}

function getInitialStep(onboardingCompleted: boolean, totalSteps: number, isLoggedIn: boolean) {
  if (onboardingCompleted && isLoggedIn) {
    return null
  }
  return onboardingCompleted ? totalSteps - 1 : 0
}

export const OnboardingModal = ({ gameId, screens }: { gameId: GameId; screens: (() => JSX.Element)[] }) => {
  const t = useTranslations()
  const loginStatus = useAppSelector((state) => state.user.loginStatus)
  const [step, setStep] = useState<number | null>(null)
  const { gameImage } = useGameImages(gameId)

  const loginStep = screens.length - 1
  const lastOnboardingScreen = screens.length - 2

  useEffect(() => {
    const onboardingCompleted = hasCompletedOnboarding()
    const initialStep = getInitialStep(onboardingCompleted, screens.length, loginStatus === LOGIN_STATUS.LOGGED_IN)
    setStep(initialStep)
  }, [loginStatus, screens.length])

  const onOnboardingComplete = () => {
    setOnboardingCompletedCookie()
    if (loginStatus === LOGIN_STATUS.LOGGED_IN) {
      setStep(null)
    } else {
      setStep(loginStep)
    }
  }

  const onNextStep = (nextStep: number) => {
    if (step === lastOnboardingScreen) {
      onOnboardingComplete()
    } else {
      setStep(nextStep)
    }
  }

  const onSkip = () => {
    if (step === loginStep) {
      setStep(null)
    } else {
      onOnboardingComplete()
    }
  }

  if (loginStatus === LOGIN_STATUS.SSO_LOGIN_MIGHT_HAPPEN) {
    return null
  }

  if (step === null) {
    return null
  }

  return (
    <LazyMotion features={features}>
      <div className={styles.Container}>
        <div className={styles.Prompt} style={getBgImage(gameId)}>
          <div className={styles.Header}>
            <Image src={gameImage('logo')} alt={gameId} width={160} />
            {step === loginStep ? (
              <button className={cx(styles.CloseButton, { [styles.CloseModal]: loginStep })} onClick={onSkip}>
                <ExitIcon />
              </button>
            ) : (
              <button className={styles.CloseButton} onClick={onSkip}>
                {t('onboarding_skip')}
              </button>
            )}
          </div>
          <div className={styles.Content}>
            <div className={styles.Screen}>
              <AnimatePresence mode="wait">{createElement(screens[step], { key: `screen-${step}` })}</AnimatePresence>
            </div>
          </div>
          <Footer
            loginStep={step === loginStep}
            lastOnboardingScreen={step === lastOnboardingScreen}
            onNextStep={() => onNextStep(step + 1)}
          />
        </div>
      </div>
    </LazyMotion>
  )
}
